import { GetStaticPropsContext, NextPage } from 'next'
import { useEffect, useMemo, useState } from 'react'
import { GlobalProps, Layout, withLayoutProps } from 'slices/Layout'

import { BaseSeo } from '@/components/seo/BaseSeo'
import { trackEcommerce } from '@/shared/analytics'
import { useLanguagesList } from '@/shared/api/generated/languages/languages'
import { storefrontPremadeStoriesList, useStorefrontPremadeStoriesList } from '@/shared/api/generated/storefront-sessions/storefront-sessions'
import { PremadeStoryList } from '@/shared/api/models/premadeStoryList'
import { Breadcrumbs } from '@/shared/components/Breadcrumbs/Breadcrumbs'
import { Icon } from '@/shared/components/Icon/Icon'
import { EMPTY_RESULT_REVALIDATE, handleStaticPropsError } from '@/shared/libs/isr'
import { pxToRem } from '@/shared/styles/themes'
import { LimitedTimeBanner } from '@/slices/HomePage/LimitedTimeBanner'
import { PremadeStoryCatalogCard } from '@/slices/Shop/PremadeStoryCatalogCard'
import { faArrowDownLong } from '@fortawesome/pro-regular-svg-icons/faArrowDownLong'
import { Box, Container, FormControl, Grid2 as Grid, InputLabel, MenuItem, Select, styled, Typography } from '@mui/material'

const REVALIDATION_FREQUENCY = 60 * 10 // 10 minutes

const DEFAULT_LANGUAGE = 'en'

export const getStaticProps = async (context: GetStaticPropsContext) => {
  let stories: PremadeStoryList[]

  try {
    stories = await storefrontPremadeStoriesList({ purchasable: true, language_code: DEFAULT_LANGUAGE })
  } catch (error) {
    // Revalidation rethrows (keeps the last good shop); only `next build` falls back to empty.
    stories = handleStaticPropsError(error, [] as PremadeStoryList[], 'shop: premade stories')
  }

  return withLayoutProps(context, {
    props: {
      stories,
    },
    revalidate: stories.length ? REVALIDATION_FREQUENCY : EMPTY_RESULT_REVALIDATE,
  })
}

type ShopPageProps = GlobalProps & {
  stories: PremadeStoryList[]
}

const ShopPage: NextPage<ShopPageProps> = ({ stories, ...layoutProps }) => {
  const [languageCode, setLanguageCode] = useState<string>(DEFAULT_LANGUAGE)

  const { data: languagesResponse } = useLanguagesList()
  const languages = useMemo(
    () => (languagesResponse?.results ?? []).slice().sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
    ),
    [languagesResponse],
  )

  const { data: clientStories } = useStorefrontPremadeStoriesList(
    { language_code: languageCode, purchasable: true },
    {
      query: {
        enabled: languageCode !== DEFAULT_LANGUAGE,
      },
    },
  )

  const displayStories: PremadeStoryList[] = useMemo(() => {
    if (languageCode === DEFAULT_LANGUAGE) return stories

    return clientStories ?? []
  }, [languageCode, stories, clientStories])

  useEffect(() => {
    if (displayStories.length > 0) {
      trackEcommerce('view_item_list', displayStories.map((story) => ({
        item_id: story.slug,
        item_name: story.title,
        item_category: 'Personalised Storybook',
      })))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode])

  return (
    <Layout {...layoutProps}>
      <BaseSeo
        title="Shop — Personalised Storybooks | Pixie World"
        description="Browse our collection of beautifully illustrated, personalised storybooks. The perfect gift for children."
      />
      <LimitedTimeBanner />
      <Container maxWidth="lg">
        <StyledPageWrapper>
          <Breadcrumbs sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              Shop
            </Typography>
          </Breadcrumbs>

          <StyledHeaderRow>
            <Box />

            {languages.length > 1 && (
              <FormControl size="small" sx={{ minWidth: pxToRem(180) }}>
                <InputLabel id="shop-language-label">Language</InputLabel>
                <Select
                  labelId="shop-language-label"
                  label="Language"
                  value={languageCode}
                  onChange={(event) => setLanguageCode(event.target.value)}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name_english}
                      {lang.name_native && lang.name_native !== lang.name_english ? ` (${lang.name_native})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </StyledHeaderRow>

          {displayStories.length > 0 ? (
            <>
              <StyledInstructionBanner>
                <Icon icon={faArrowDownLong} sx={{ fontSize: pxToRem(28) }} />
                <Typography variant="h5" fontWeight={700} color="inherit">
                  Pick a story below and customise it now
                </Typography>
              </StyledInstructionBanner>

              <Grid container spacing={3}>
                {displayStories.map((story) => (
                  <Grid key={story.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <PremadeStoryCatalogCard story={story} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              No stories available in this language yet.
            </Typography>
          )}
        </StyledPageWrapper>
      </Container>
    </Layout>
  )
}

export default ShopPage

const StyledPageWrapper = styled(Box)`
  padding: ${pxToRem(32)} 0 ${pxToRem(64)};
`

const StyledInstructionBanner = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: `${pxToRem(20)} ${pxToRem(24)}`,
  marginBottom: theme.spacing(3),
  borderRadius: pxToRem(16),
  backgroundColor: theme.palette.custom.warmCream,
  border: `2px solid ${theme.palette.custom.sand}`,
  color: theme.palette.custom.deepTerracotta,
  textAlign: 'center',

  [theme.breakpoints.down('sm')]: {
    padding: `${pxToRem(16)} ${pxToRem(16)}`,
    gap: theme.spacing(1.5),
  },
}))

const StyledHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
  },
}))
